import {transcriptionWords} from "../../protocol/transcription-words";
export {transcriptionWords} from "../../protocol/transcription-words";
import { captionSourceDocument } from "../../protocol/caption-source";
import type { UndoableState } from "../../vendor/src/features/remotion-editor/editor/state/types";
import type { AudioItem } from "../../vendor/src/features/remotion-editor/editor/items/audio/audio-item-type";
import type { CaptionsItem } from "../../vendor/src/features/remotion-editor/editor/items/captions/captions-item-type";
import { migrateLegacyDocument } from "./migrate-legacy-document";

export function detachVideoAudio(
  document: UndoableState,
  ids: string[],
  id: () => string,
): UndoableState {
  const clips = [...new Set(ids)]
    .map((key) => document.items[key])
    .filter((item) => item?.type === "video");
  if (!clips.length) throw Error("소리를 분리할 영상을 선택해 주세요.");
  const next = {
    ...document,
    items: { ...document.items },
    assets: { ...document.assets },
    tracks: [...document.tracks],
  };
  for (const clip of clips) {
    const track = document.tracks.find((t) => t.items.includes(clip.id));
    if (!track || track.locked)
      throw Error("선택한 영상 트랙의 잠금을 먼저 풀어 주세요.");
    if (clip.muteAudio)
      throw Error(
        "이미 소리가 꺼진 영상입니다. 분리된 오디오 트랙을 확인해 주세요.",
      );
    const asset = document.assets[clip.assetId];
    if (asset?.type !== "video" || !asset.hasAudioTrack)
      throw Error("선택한 영상에 오디오가 없습니다.");
    const assetId = id(),
      itemId = id();
    next.assets[assetId] = {
      id: assetId,
      type: "audio",
      filename: asset.filename,
      size: asset.size,
      mimeType: asset.mimeType,
      remoteUrl: asset.remoteUrl,
      remoteFileKey: asset.remoteFileKey,
      durationInSeconds: asset.durationInSeconds,
    };
    const audio: AudioItem = {
      id: itemId,
      type: "audio",
      timelineSourceItemId: clip.id,
      assetId,
      from: clip.from,
      durationInFrames: clip.durationInFrames,
      top: 0,
      left: 0,
      width: 0,
      height: 0,
      opacity: 1,
      isDraggingInTimeline: false,
      audioStartFromInSeconds: clip.videoStartFromInSeconds,
      playbackRate: clip.playbackRate,
      decibelAdjustment: clip.decibelAdjustment,
      audioFadeInDurationInSeconds: clip.audioFadeInDurationInSeconds,
      audioFadeOutDurationInSeconds: clip.audioFadeOutDurationInSeconds,
      ...(clip.audioFadeWindow
        ? { audioFadeWindow: { ...clip.audioFadeWindow } }
        : {}),
    };
    next.items[itemId] = audio;
    next.items[clip.id] = { ...clip, muteAudio: true };
    next.tracks.push({
      id: id(),
      items: [itemId],
      locked: false,
      hidden: track.hidden,
      muted: track.muted,
      magnetic: false,
      zone: "audio",
      metadata: { type: "audio", isMainTrack: false, trackType: null },
    });
  }
  return next;
}

export function importSourceCaptions(
  document: UndoableState,
  legacy: unknown,
  id: () => string,
): UndoableState {
  if (Object.values(document.items).some((i) => i.type === "captions"))
    throw Error(
      "이미 자막이 있습니다. 기존 자막을 보존하기 위해 중복으로 가져오지 않았습니다.",
    );
  const migrated = migrateLegacyDocument(captionSourceDocument(legacy), {});
  if (!migrated.ok) throw Error("기존 자막 자료를 읽지 못했습니다.");
  const source = migrated.document;
  if (
    source.fps !== document.fps ||
    source.compositionWidth !== document.compositionWidth ||
    source.compositionHeight !== document.compositionHeight
  )
    throw Error(
      "앞단과 영상 크기·프레임이 다릅니다. 원래 작업 설정을 맞춘 뒤 다시 가져와 주세요.",
    );
  const next = {
    ...document,
    items: { ...document.items },
    assets: { ...document.assets },
    tracks: [...document.tracks],
  };
  let count = 0;
  for (const track of source.tracks) {
    const items: string[] = [];
    for (const key of track.items) {
      const item = source.items[key];
      if (item?.type !== "captions") continue;
      const asset = source.assets[item.assetId];
      if (asset?.type !== "caption") continue;
      const assetId = id(),
        itemId = id();
      next.assets[assetId] = { ...asset, id: assetId };
      next.items[itemId] = { ...item, id: itemId, assetId };
      items.push(itemId);
      count++;
    }
    if (items.length)
      next.tracks.unshift({ ...track, id: id(), items, locked: false });
  }
  if (!count)
    throw Error(
      "앞단에 저장된 자막이 없습니다. 자동자막을 사용하거나 원래 화면에서 자막을 저장해 주세요.",
    );
  return next;
}

export function transcriptionClip(document: UndoableState, clipId: string) {
  const clip = document.items[clipId];
  if (!clip || (clip.type !== "audio" && clip.type !== "video"))
    throw Error("영상이나 음성을 하나 선택해 주세요.");
  const main = document.tracks.find(
    (t) => t.metadata?.type === "caption" && t.metadata.isMainTrack,
  );
  if (
    main?.locked ||
    document.tracks.some((t) => t.locked && t.items.includes(clipId))
  )
    throw Error("선택한 트랙과 자막 트랙의 잠금을 풀어 주세요.");
  const active = document.tracks.flatMap((t) => t.items);
  if (
    active.some((key) => {
      const i = document.items[key];
      return (
        i?.type === "captions" &&
        i.from < clip.from + clip.durationInFrames &&
        i.from + i.durationInFrames > clip.from
      );
    })
  )
    throw Error(
      "이 구간에 이미 자막이 있습니다. 기존 자막을 수정하거나 해당 자막을 삭제한 뒤 다시 생성해 주세요.",
    );
  return clip;
}

type Word = { text: string; start: number; end: number };

export function addTranscribedCaptions(
  document: UndoableState,
  clipId: string,
  raw: unknown,
  id: () => string,
): UndoableState {
  const clip = transcriptionClip(document, clipId);
  if (document.tracks.some((t) => t.locked && t.items.includes(clipId)))
    throw Error("선택한 트랙의 잠금을 풀어 주세요.");
  const words = transcriptionWords((raw as { words?: unknown })?.words);
  const rate = clip.playbackRate,
    start =
      clip.type === "video"
        ? clip.videoStartFromInSeconds
        : clip.audioStartFromInSeconds;
  if (!Number.isFinite(rate) || rate <= 0)
    throw Error("클립 재생 속도를 확인해 주세요.");
  const end = start + (clip.durationInFrames / document.fps) * rate;
  const captions = words
    .filter(
      (w): w is Word =>
        !!w &&
        typeof w.text === "string" &&
        w.text.trim().length > 0 &&
        Number.isFinite(w.start) &&
        Number.isFinite(w.end) &&
        w.end > w.start &&
        w.end > start &&
        w.start < end,
    )
    .map((w) => ({
      text: w.text,
      startMs: ((Math.max(start, w.start) - start) / rate) * 1000,
      endMs: ((Math.min(end, w.end) - start) / rate) * 1000,
      timestampMs: null,
      confidence: null,
    }))
    .sort((a, b) => a.startMs - b.startMs);
  if (!captions.length) throw Error("선택한 구간에서 인식한 음성이 없습니다.");
  const assetId = id(),
    itemId = id(),
    fontSize = Math.round(document.compositionWidth * 0.06);
  const item=createTranscribedCaptionItem(document,itemId,assetId,clip.from,clip.durationInFrames);
  return {
    ...document,
    assets: {
      ...document.assets,
      [assetId]: {
        id: assetId,
        type: "caption",
        filename: "자동자막.json",
        size: JSON.stringify(captions).length,
        remoteUrl: null,
        remoteFileKey: null,
        mimeType: "application/json",
        captions,
      },
    },
    items: { ...document.items, [itemId]: item },
    tracks: document.tracks.some(
      (t) => t.metadata?.type === "caption" && t.metadata.isMainTrack,
    )
      ? document.tracks.map((t) =>
          t ===
          document.tracks.find(
            (t) => t.metadata?.type === "caption" && t.metadata.isMainTrack,
          )
            ? {
                ...t,
                items: [...t.items, itemId].sort(
                  (a, b) =>
                    (a === itemId ? item.from : document.items[a]!.from) -
                    (b === itemId ? item.from : document.items[b]!.from),
                ),
              }
            : t,
        )
      : [
          {
            id: id(),
            items: [itemId],
            locked: false,
            hidden: false,
            muted: false,
            magnetic: false,
            zone: "visual",
            metadata: { type: "caption", isMainTrack: true, trackType: "main" },
          },
          ...document.tracks,
        ],
  };
}

/** Native automatic caption style shared with scene voice insertion. */
export function createTranscribedCaptionItem(document:UndoableState,itemId:string,assetId:string,from:number,durationInFrames:number):CaptionsItem{
  const fontSize=Math.round(document.compositionWidth*.06);
  return {
    id: itemId,
    type: "captions",
    isMainTrack: true,
    assetId,
    from: from,
    durationInFrames: durationInFrames,
    top: document.compositionHeight * 0.75,
    left: document.compositionWidth * 0.05,
    width: document.compositionWidth * 0.9,
    height: fontSize * 2.4,
    opacity: 1,
    isDraggingInTimeline: false,
    rotation: 0,
    fontFamily: "Pretendard",
    fontStyle: { weight: "600", variant: "normal" },
    fontSize,
    lineHeight: 1.2,
    letterSpacing: 0,
    color: "#ffffff",
    highlightColor: "#ffffff",
    align: "center",
    direction: "ltr",
    strokeColor: "#000000",
    strokeWidth: 2,
    maxLines: 2,
    pageDurationInMilliseconds: 2500,
    captionStartInSeconds: 0,
    fadeInDurationInSeconds: 0,
    fadeOutDurationInSeconds: 0,
  };
}
